<template>
  <dx-data-grid
    ref="dataGridRef"
    :allow-column-reordering="true"
    :column-chooser="{ enabled: true, mode: 'select' }"
    :column-min-width="50"
    :columns="columns"
    :data-source="dataSource"
    :filter-panel="{ visible: true }"
    :filter-row="{ visible: true }"
    :header-filter="{ visible: true }"
    :height="50"
    :hover-state-enabled="true"
    :remote-operations="true"
    :row-alternation-enabled="true"
    :show-borders="true"
    :show-column-lines="true"
    :width="'100%'"
    :word-wrap-enabled="true"
    style="flex-grow: 1"
    @toolbar-preparing="onToolbarPreparing($event)"
    @selection-changed="selectionChanged($event)"
    @cell-prepared="onCellPrepared($event)"
    @content-ready="onContentReady($event)"
    @cell-dbl-click="openDetail($event)"
  >
    <dx-paging :enabled="true" :page-size="30" />
    <dx-load-panel :enabled="true" />
    <dx-scrolling mode="virtual" />
    <dx-sorting mode="single" />
    <dx-export :allow-export-selected-data="true" :enabled="true" :formats="['pdf', 'excel']" />
    <dx-selection
      mode="single"
    />

    <template #filterYearTemplate>
      <div class="mr-4">
        <span>Год расчета:</span>
        <dx-select-box
          v-model="filter.year"
          :data-source="yearDataSource"
          :showClearButton="false"
          width="120"
          @valueChanged="reloadData()"
        />
      </div>
    </template>

    <template #filterTemplatePeriod>
      <span>Базовый период:</span>
      <mped-calculation-period-component
        :default-value="filter.period"
        :periodFnSource="getUploadPeriods"
        :selectionMode="'single'"
        :show-clear-button="false"
        @value-changed="(e) => {
        filter.period = e && e[0] || null;
        reloadData();
      }"
      />
    </template>

    <template #longTextCellTemplate="{ data }">
      <long-text-template
        :data="data.data"
      />
    </template>

  </dx-data-grid>

  <footer-grid :totalCount="totalCount" class="px-2"></footer-grid>

  <mped-popup-grid-view
    ref="popupHistory"
    :columns="columnsAmortizationCalculationHistory"
    :fn-get-data="getCalculationHistory"
    popupTitle="История расчета"
  />

  <mped-popup-grid-view
    ref="popupDetail"
    :columns="columnsAmortizationCalculationDetail"
    :fn-get-data="getCalculationDetail"
    :popup-height="'70vh'"
    :show-summary="true"
    popupTitle="Детализация распределения"

  />

</template>

<script>
export default {
  name: 'mped-amortization-calculation-view',
};
</script>

<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  DxSelectBox,
} from 'devextreme-vue';
import {
  DxDataGrid,
  DxScrolling,
  DxLoadPanel,
  DxSorting,
  DxExport,
  DxPaging,
  DxSelection,
} from 'devextreme-vue/data-grid';

import FooterGrid from '@/components/grid-card-component/footer-grid.vue';
import { getAiToolbar } from '@/components/utils/ai-toolbar-generator';
import { getAiDataSource } from '@/components/utils/ai-data-source-generator';
import {
  getCalculation, getCalculationDetail, getCalculationHistory, getUploadPeriods, setCalculation, setCalculationValidate,
} from '@/views/mped/services/mped-amortization-api-service';
import {
  columnsAmortizationCalculation, columnsAmortizationCalculationDetail, columnsAmortizationCalculationHistory,
} from '@/views/mped/services/mped-amortization-service';
import { getYearsList } from '@/views/mped/services/mped-estimate-service';
import MpedCalculationPeriodComponent
  from '@/views/mped/views/calculation/component/mped-calculation-period-component.vue';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import { dxUiNotify } from '@/utils/dx-helper';
import { hideLoadPanel, showLoadPanel } from '@/components/utils/ai-global-load-panel';
import { svgAiLibrary } from '@/service/svg-ai-library';
import MpedPopupGridView from '@/views/mped/components/mped-popup-grid-view/mped-popup-grid-view.vue';
import { setLocalUserdata, initState, hasEditablePermissionByUIRoute } from '@/service/local-userdata-service';
import LongTextTemplate from '@/components/cell-template-components/long-text-template.vue';

const
  totalCount = ref(0),
  columns = columnsAmortizationCalculation,
  yearDataSource = getYearsList((new Date()).getFullYear(), 5, 5),
  filter = ref({ period: null, year: (new Date()).getFullYear() }),
  popupHistory = ref(),
  popupDetail = ref(),
  dataGridRef = ref(),
  router = useRouter();
const isEditable = hasEditablePermissionByUIRoute(router);

let
  selectedRowsData = [],
  toolbarButtons = {};

const dataSource = getAiDataSource(
  {
    dataFn: getCalculation,
    key: 'id',
    remoteOperation: true,
    validationFn: () => {
      return filter.value.year || filter.value.period;
    },
    customFilterFn: () => {
      return [['year', '=', filter.value.year], 'and', ['period', '=', filter.value.period]];
    },
    onLoadedFn: (data) => {
      totalCount.value = data.totalCount;
    },
    defaultDescSort: false,
    dateFields: ['date_receipts'],
    baseSort: [
      { 'selector': 'department_id', 'desc': false },
      { 'selector': 'main_asset_id', 'desc': false }],
    //  columns,
  },
);

onMounted(async () => {
  initState(router, dataGridRef, filter);
  setColumnCaption();
});

const onToolbarPreparing = (e) => {
  e.toolbarOptions.elementAttr = {
    class: 'ai-toolbar-row-with-label',
  };

  toolbarButtons = getAiToolbar(e, {
    refresh: {
      event: reloadData,
    },
    customs: [
      {
        location: 'before',
        template: 'filterYearTemplate',
      }, {
        location: 'before',
        template: 'filterTemplatePeriod',
      }, {
        visible: true,
        widget: 'dxButton',
        location: 'before',
        name: 'amortizationCalculate',
        cssClass: 'toolbar-item-custom-style',
        options: {
          disabled: !isEditable,
          icon: svgAiLibrary['ai-calculator-base'],
          hint: 'Запустить расчет амортизации',
          onClick: calculateAmortization,
        },
      }, {
        visible: true,
        widget: 'dxButton',
        location: 'before',
        name: 'amortizationCalculate',
        cssClass: 'toolbar-item-custom-style',
        options: {
          icon: svgAiLibrary['ai-history-base'],
          hint: 'История расчета',
          onClick: showHistory,
        },
      }],
  });
};

const reloadData = () => {
  setColumnCaption();
  dataSource.reload();
};

const setColumnCaption = () => {
  dataGridRef.value.instance.columnOption('amortization_by_year', 'caption', `Амортизация за ${filter.value.year} год (факт). руб`);
};

const selectionChanged = (e) => {
  selectedRowsData = e.selectedRowsData;
  if (toolbarButtons.check)
    toolbarButtons.check.option({ disabled: !selectedRowsData.length });
};

const calculateAmortization = async (e) => {
  if (!filter.value.year || !filter.value.period) {
    dxUiNotify('Необходимо указать год и период', 'error');
    return;
  }

  if (await AiCustomConfirmDialog(
    `Расчитать амортизацию на ${filter.value.year} на основе данных за перод ${filter.value.period}?`,
    'Расчет амортизации', true, false)) {
    showLoadPanel();
    try {
      const validation = await setCalculationValidate(filter.value);
      if (validation.success && validation.processId) {
        const result = await setCalculation({ ...filter.value, processId: validation.processId });

        if (result.success)
          reloadData();
      }
    } finally {
      hideLoadPanel();
    }
  }
};

const onCellPrepared = (e) => {
  if (e.rowType === 'data' && e.row.data.type_row_id === 3) {
    e.cellElement.classList.add('mped-calculation-department-total');
  }
};

const showHistory = () => {
  popupHistory.value.Open();
};

const onContentReady = (e) => {
  setLocalUserdata(router, dataGridRef, filter.value);
};

const openDetail = (e) => {
  if (e.rowType === 'data' && e.column.detail) {
    popupDetail.value.Open({ params: { rowId: e.data.id }, needReload: true });
  }
};

</script>

<style>
.mped-calculation-department-total {
  color: #0b4c8c;
  font-weight: bold;
}
</style>
